import { useEffect, useMemo } from 'react';

import type { PosShortcutAction, PosShortcutDefinition, PosShortcutMap } from '../state/types';
import { usePosUiState } from './usePosUiState';

interface UsePosKeyboardShortcutsOptions {
  onFocusSearch: () => void;
  onOpenDiscounts?: () => void;
  onOpenPayment?: () => void;
  onConfirm?: () => void;
  onOpenAdvancedSearch?: () => void;
  isConfirmDisabled?: boolean;
  isCartEmpty?: boolean;
}

export interface UsePosKeyboardShortcutsResult {
  shortcuts: PosShortcutMap;
  setShortcutsForAction: (action: PosShortcutAction, shortcuts: PosShortcutDefinition[]) => void;
  resetShortcutsForAction: (action: PosShortcutAction) => void;
}

const isTextInput = (element: Element | null): boolean => {
  if (!element) {
    return false;
  }

  const tag = element.tagName;
  if (tag === 'INPUT') {
    const input = element as HTMLInputElement;
    return input.type !== 'checkbox' && input.type !== 'radio' && !input.readOnly;
  }

  if (tag === 'TEXTAREA') {
    return !(element as HTMLTextAreaElement).readOnly;
  }

  if ((element as HTMLElement).isContentEditable) {
    return true;
  }

  return false;
};

const normalizeKey = (key: string): string => {
  return key.toLowerCase();
};

const matchesShortcut = (event: KeyboardEvent, shortcut: PosShortcutDefinition): boolean => {
  const eventKey = normalizeKey(event.key);
  const shortcutKey = normalizeKey(shortcut.key);
  if (eventKey !== shortcutKey) {
    return false;
  }

  if (shortcut.system && !(event.metaKey || event.ctrlKey)) {
    return false;
  }
  if (shortcut.meta && !event.metaKey) {
    return false;
  }
  if (shortcut.ctrl && !event.ctrlKey) {
    return false;
  }
  if (shortcut.alt && !event.altKey) {
    return false;
  }
  if (shortcut.shift && !event.shiftKey) {
    return false;
  }

  return true;
};

const matchesAction = (event: KeyboardEvent, shortcuts: PosShortcutDefinition[] | undefined): boolean => {
  if (!shortcuts || shortcuts.length === 0) {
    return false;
  }
  return shortcuts.some((shortcut) => matchesShortcut(event, shortcut));
};

export const usePosKeyboardShortcuts = ({
  onFocusSearch,
  onOpenDiscounts,
  onOpenPayment,
  onConfirm,
  onOpenAdvancedSearch,
  isConfirmDisabled = false,
  isCartEmpty = false,
}: UsePosKeyboardShortcutsOptions): UsePosKeyboardShortcutsResult => {
  const { shortcuts, setShortcutsForAction, resetShortcutsForAction } = usePosUiState();

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const activeElement = document.activeElement;

      if (matchesAction(event, shortcuts.openAdvancedSearch) && onOpenAdvancedSearch) {
        event.preventDefault();
        onFocusSearch();
        onOpenAdvancedSearch();
        return;
      }

      if (matchesAction(event, shortcuts.focusSearch)) {
        event.preventDefault();
        onFocusSearch();
        return;
      }

      if (matchesAction(event, shortcuts.openDiscounts) && onOpenDiscounts) {
        event.preventDefault();
        onOpenDiscounts();
        return;
      }

      if (matchesAction(event, shortcuts.openPayment) && onOpenPayment) {
        event.preventDefault();
        if (!isCartEmpty) {
          onOpenPayment();
        }
        return;
      }

      if (matchesAction(event, shortcuts.confirm) && onConfirm) {
        if (isTextInput(activeElement)) {
          return;
        }
        if (isConfirmDisabled || isCartEmpty) {
          return;
        }
        event.preventDefault();
        onConfirm();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [
    isCartEmpty,
    isConfirmDisabled,
    onConfirm,
    onFocusSearch,
    onOpenAdvancedSearch,
    onOpenDiscounts,
    onOpenPayment,
    shortcuts,
  ]);
  return useMemo(
    () => ({
      shortcuts,
      setShortcutsForAction,
      resetShortcutsForAction,
    }),
    [resetShortcutsForAction, setShortcutsForAction, shortcuts],
  );
};

export default usePosKeyboardShortcuts;
